import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import './Stats.css';

const statsData = [
  { value: 50,   suffix: '+',  label: 'Enterprise Clients',   decimals: 0 },
  { value: 99.9, suffix: '%',  label: 'Uptime SLA',           decimals: 1 },
  { value: 150,  suffix: '+',  label: 'Projects Delivered',   decimals: 0 },
  { value: 24,   suffix: '/7', label: 'Support Coverage',     decimals: 0 },
  { value: 5,    suffix: '+',  label: 'Years Experience',     decimals: 0 },
  { value: 3,    suffix: 'x',  label: 'Faster Deployment',    decimals: 0 },
];

/* ── Custom counter hook ── */
function useCountUp(target, decimals, active, duration = 1800) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return;

    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const elapsed = timestamp - startTimestamp;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(parseFloat((eased * target).toFixed(decimals)));
      if (progress < 1) requestAnimationFrame(step);
    };

    const raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, target, decimals, duration]);

  return count;
}

/* ── Single stat card ── */
function StatCard({ stat, index, active }) {
  const count = useCountUp(stat.value, stat.decimals, active);

  return (
    <motion.div
      className="stat-card"
      initial={{ opacity: 0, y: 24 }}
      animate={active ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <span className="stat-number">
        {stat.decimals > 0 ? count.toFixed(stat.decimals) : Math.round(count)}
        <span className="accent">{stat.suffix}</span>
      </span>
      <p className="stat-label">{stat.label}</p>
      <motion.div
        className="stat-line"
        initial={{ scaleX: 0 }}
        animate={active ? { scaleX: 1 } : {}}
        transition={{ duration: 0.6, delay: 0.3 + index * 0.08, ease: 'easeOut' }}
      />
    </motion.div>
  );
}

/* ── Main component ── */
export default function Stats() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (isInView && !hasAnimated) setHasAnimated(true);
  }, [isInView, hasAnimated]);

  return (
    <section className="stats-section" ref={sectionRef} aria-labelledby="stats-heading">
      <div className="container">
        <div className="stats-intro">
          <h2 id="stats-heading" className="stats-heading">
            Trusted by Enterprises Across <span className="accent">India</span>
          </h2>
          <p className="stats-sub">Delivering measurable results through engineering excellence and 24/7 partnership.</p>
        </div>
        <div className="stats-grid">
          {statsData.map((stat, i) => (
            <StatCard key={stat.label} stat={stat} index={i} active={hasAnimated} />
          ))}
        </div>
      </div>
    </section>
  );
}